import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { Plus, Trash2, Edit2, ArrowLeft, Loader2 } from 'lucide-react';

export default function MasterData() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clients');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Clients state
  const [clients, setClients] = useState([]);
  const [showClientDialog, setShowClientDialog] = useState(false);
  const [clientForm, setClientForm] = useState({ id: '', clientName: '', industry: '', country: '', description: '', isActive: true });
  const [editingClientId, setEditingClientId] = useState(null);

  // Project Leads state
  const [projectLeads, setProjectLeads] = useState([]);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [leadForm, setLeadForm] = useState({ id: '', name: '', email: '', phoneNumber: '', department: '', isActive: true });
  const [editingLeadId, setEditingLeadId] = useState(null);

  // Project Types state
  const [projectTypes, setProjectTypes] = useState([]);
  const [showTypeDialog, setShowTypeDialog] = useState(false);
  const [typeForm, setTypeForm] = useState({ id: '', typeValue: '', typeLabel: '', description: '', isActive: true });
  const [editingTypeId, setEditingTypeId] = useState(null);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ id: '', categoryValue: '', categoryLabel: '', description: '', isActive: true });
  const [editingCategoryId, setEditingCategoryId] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'clients') {
        const data = await api.listClients(false);
        setClients(data);
      } else if (activeTab === 'leads') {
        const data = await api.listProjectLeads(false);
        setProjectLeads(data);
      } else if (activeTab === 'types') {
        const data = await api.listProjectTypes(false);
        setProjectTypes(data);
      } else if (activeTab === 'categories') {
        const data = await api.listCategories(false);
        setCategories(data);
      }
    } catch (err) {
      toast.error(`Failed to load ${activeTab}`);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // CLIENTS FUNCTIONS
  // ============================================================================

  const openClientDialog = (client = null) => {
    if (client) {
      setClientForm(client);
      setEditingClientId(client.id);
    } else {
      setClientForm({ id: '', clientName: '', industry: '', country: '', description: '', isActive: true });
      setEditingClientId(null);
    }
    setShowClientDialog(true);
  };

  const saveClient = async () => {
    if (!clientForm.clientName.trim()) {
      toast.error('Client name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingClientId) {
        const updated = await api.updateClient(editingClientId, clientForm);
        setClients(clients.map(c => c.id === editingClientId ? updated : c));
        toast.success('Client updated successfully');
      } else {
        const created = await api.createClient(clientForm);
        setClients([...clients, created]);
        toast.success('Client created successfully');
      }
      setShowClientDialog(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const deleteClient = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    setSaving(true);
    try {
      await api.deleteClient(clientId);
      setClients(clients.filter(c => c.id !== clientId));
      toast.success('Client deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete client');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // PROJECT LEADS FUNCTIONS
  // ============================================================================

  const openLeadDialog = (lead = null) => {
    if (lead) {
      setLeadForm(lead);
      setEditingLeadId(lead.id);
    } else {
      setLeadForm({ id: '', name: '', email: '', phoneNumber: '', department: '', isActive: true });
      setEditingLeadId(null);
    }
    setShowLeadDialog(true);
  };

  const saveLead = async () => {
    if (!leadForm.name.trim()) {
      toast.error('Lead name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingLeadId) {
        const updated = await api.updateProjectLead(editingLeadId, leadForm);
        setProjectLeads(projectLeads.map(l => l.id === editingLeadId ? updated : l));
        toast.success('Project lead updated successfully');
      } else {
        const created = await api.createProjectLead(leadForm);
        setProjectLeads([...projectLeads, created]);
        toast.success('Project lead created successfully');
      }
      setShowLeadDialog(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save project lead');
    } finally {
      setSaving(false);
    }
  };

  const deleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this project lead?')) return;

    setSaving(true);
    try {
      await api.deleteProjectLead(leadId);
      setProjectLeads(projectLeads.filter(l => l.id !== leadId));
      toast.success('Project lead deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete project lead');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // PROJECT TYPES FUNCTIONS
  // ============================================================================

  const openTypeDialog = (type = null) => {
    if (type) {
      setTypeForm(type);
      setEditingTypeId(type.id);
    } else {
      setTypeForm({ id: '', typeValue: '', typeLabel: '', description: '', isActive: true });
      setEditingTypeId(null);
    }
    setShowTypeDialog(true);
  };

  const saveType = async () => {
    if (!typeForm.typeValue.trim() || !typeForm.typeLabel.trim()) {
      toast.error('Type value and label are required');
      return;
    }

    setSaving(true);
    try {
      if (editingTypeId) {
        const updated = await api.updateProjectType(editingTypeId, typeForm);
        setProjectTypes(projectTypes.map(t => t.id === editingTypeId ? updated : t));
        toast.success('Project type updated successfully');
      } else {
        const created = await api.createProjectType(typeForm);
        setProjectTypes([...projectTypes, created]);
        toast.success('Project type created successfully');
      }
      setShowTypeDialog(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save project type');
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this project type?')) return;

    setSaving(true);
    try {
      await api.deleteProjectType(typeId);
      setProjectTypes(projectTypes.filter(t => t.id !== typeId));
      toast.success('Project type deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete project type');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================================
  // CATEGORIES FUNCTIONS
  // ============================================================================

  const openCategoryDialog = (category = null) => {
    if (category) {
      setCategoryForm(category);
      setEditingCategoryId(category.id);
    } else {
      setCategoryForm({ id: '', categoryValue: '', categoryLabel: '', description: '', isActive: true });
      setEditingCategoryId(null);
    }
    setShowCategoryDialog(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.categoryValue.trim() || !categoryForm.categoryLabel.trim()) {
      toast.error('Category value and label are required');
      return;
    }

    setSaving(true);
    try {
      if (editingCategoryId) {
        const updated = await api.updateCategory(editingCategoryId, categoryForm);
        setCategories(categories.map(c => c.id === editingCategoryId ? updated : c));
        toast.success('Category updated successfully');
      } else {
        const created = await api.createCategory(categoryForm);
        setCategories([...categories, created]);
        toast.success('Category created successfully');
      }
      setShowCategoryDialog(false);
    } catch (err) {
      toast.error(err.message || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;

    setSaving(true);
    try {
      await api.deleteCategory(categoryId);
      setCategories(categories.filter(c => c.id !== categoryId));
      toast.success('Category deleted successfully');
    } catch (err) {
      toast.error(err.message || 'Failed to delete category');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'clients', label: 'Clients', icon: '🏢' },
    { id: 'leads', label: 'Project Leads', icon: '👤' },
    { id: 'types', label: 'Project Types', icon: '📋' },
    { id: 'categories', label: 'Categories', icon: '🏷️' },
  ];

  return (
    <div className="min-h-screen bg-black px-3 sm:px-4 md:px-6 py-6 md:py-8">
      <Toaster theme="dark" />

      {/* Header */}
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-zinc-400 hover:text-white hover:bg-zinc-900 flex-shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="font-manrope font-bold text-xl sm:text-2xl md:text-3xl text-white">Master Data</h1>
            <p className="text-zinc-500 text-xs sm:text-sm mt-1">Manage clients, project leads, types, and categories</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-zinc-800">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id
              ? 'bg-brand-500 text-white hover:bg-brand-400 rounded-b-none'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-b-none'
            }
          >
            {tab.icon} {tab.label}
          </Button>
        ))}
      </div>

      {/* Content */}
      <Card className="bg-zinc-950 border-zinc-900">
        <CardHeader className="pb-4 flex flex-row items-center justify-between">
          <CardTitle className="font-manrope font-bold text-base text-white">
            {tabs.find(t => t.id === activeTab)?.label}
          </CardTitle>
          <Button
            size="sm"
            onClick={() => {
              if (activeTab === 'clients') openClientDialog();
              else if (activeTab === 'leads') openLeadDialog();
              else if (activeTab === 'types') openTypeDialog();
              else if (activeTab === 'categories') openCategoryDialog();
            }}
            className="bg-brand-500 text-white hover:bg-brand-400"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-6 h-6 animate-spin text-brand-400" />
            </div>
          ) : (
            <>
              {/* CLIENTS TAB */}
              {activeTab === 'clients' && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Name</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Industry</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Country</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Status</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {clients.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="5" className="p-4 text-center text-zinc-500">No clients yet</TableCell>
                        </TableRow>
                      ) : (
                        clients.map(client => (
                          <TableRow key={client.id} className="border-zinc-900 hover:bg-zinc-900/30">
                            <TableCell className="p-3 font-manrope text-sm">{client.clientName}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{client.industry || '-'}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{client.country || '-'}</TableCell>
                            <TableCell className="p-3">
                              <Badge className={client.isActive ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30'}>
                                {client.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-3 flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openClientDialog(client)} className="h-8 w-8 p-0">
                                <Edit2 className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteClient(client.id)} disabled={saving} className="h-8 w-8 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* PROJECT LEADS TAB */}
              {activeTab === 'leads' && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Name</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Email</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Phone</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Department</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Status</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectLeads.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="6" className="p-4 text-center text-zinc-500">No project leads yet</TableCell>
                        </TableRow>
                      ) : (
                        projectLeads.map(lead => (
                          <TableRow key={lead.id} className="border-zinc-900 hover:bg-zinc-900/30">
                            <TableCell className="p-3 font-manrope text-sm">{lead.name}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{lead.email || '-'}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{lead.phoneNumber || '-'}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{lead.department || '-'}</TableCell>
                            <TableCell className="p-3">
                              <Badge className={lead.isActive ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30'}>
                                {lead.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-3 flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openLeadDialog(lead)} className="h-8 w-8 p-0">
                                <Edit2 className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteLead(lead.id)} disabled={saving} className="h-8 w-8 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* PROJECT TYPES TAB */}
              {activeTab === 'types' && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Value</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Label</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Description</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Status</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectTypes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="5" className="p-4 text-center text-zinc-500">No project types yet</TableCell>
                        </TableRow>
                      ) : (
                        projectTypes.map(type => (
                          <TableRow key={type.id} className="border-zinc-900 hover:bg-zinc-900/30">
                            <TableCell className="p-3 font-mono text-sm text-zinc-300">{type.typeValue}</TableCell>
                            <TableCell className="p-3 font-manrope text-sm">{type.typeLabel}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{type.description || '-'}</TableCell>
                            <TableCell className="p-3">
                              <Badge className={type.isActive ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30'}>
                                {type.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-3 flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openTypeDialog(type)} className="h-8 w-8 p-0">
                                <Edit2 className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteType(type.id)} disabled={saving} className="h-8 w-8 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* CATEGORIES TAB */}
              {activeTab === 'categories' && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-800 hover:bg-transparent">
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Value</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Label</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Description</TableHead>
                        <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Status</TableHead>
                        <TableHead className="w-20"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan="5" className="p-4 text-center text-zinc-500">No categories yet</TableCell>
                        </TableRow>
                      ) : (
                        categories.map(category => (
                          <TableRow key={category.id} className="border-zinc-900 hover:bg-zinc-900/30">
                            <TableCell className="p-3 font-mono text-sm text-zinc-300">{category.categoryValue}</TableCell>
                            <TableCell className="p-3 font-manrope text-sm">{category.categoryLabel}</TableCell>
                            <TableCell className="p-3 text-sm text-zinc-400">{category.description || '-'}</TableCell>
                            <TableCell className="p-3">
                              <Badge className={category.isActive ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-zinc-600/20 text-zinc-400 border-zinc-600/30'}>
                                {category.isActive ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-3 flex gap-2">
                              <Button size="sm" variant="ghost" onClick={() => openCategoryDialog(category)} className="h-8 w-8 p-0">
                                <Edit2 className="w-3.5 h-3.5 text-zinc-400 hover:text-white" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteCategory(category.id)} disabled={saving} className="h-8 w-8 p-0">
                                <Trash2 className="w-3.5 h-3.5 text-zinc-600 hover:text-red-400" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* CLIENT DIALOG */}
      <Dialog open={showClientDialog} onOpenChange={setShowClientDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {editingClientId ? 'Edit Client' : 'Add New Client'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Client Name *</Label>
              <Input
                value={clientForm.clientName}
                onChange={e => setClientForm({ ...clientForm, clientName: e.target.value })}
                placeholder="Enter client name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Industry</Label>
              <Input
                value={clientForm.industry || ''}
                onChange={e => setClientForm({ ...clientForm, industry: e.target.value })}
                placeholder="Enter industry"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Country</Label>
              <Input
                value={clientForm.country || ''}
                onChange={e => setClientForm({ ...clientForm, country: e.target.value })}
                placeholder="Enter country"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Description</Label>
              <Input
                value={clientForm.description || ''}
                onChange={e => setClientForm({ ...clientForm, description: e.target.value })}
                placeholder="Enter description"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowClientDialog(false)} className="text-zinc-400 hover:text-white">
              Cancel
            </Button>
            <Button onClick={saveClient} disabled={saving} className="bg-brand-500 text-white hover:bg-brand-400">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* LEAD DIALOG */}
      <Dialog open={showLeadDialog} onOpenChange={setShowLeadDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {editingLeadId ? 'Edit Project Lead' : 'Add New Project Lead'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Name *</Label>
              <Input
                value={leadForm.name}
                onChange={e => setLeadForm({ ...leadForm, name: e.target.value })}
                placeholder="Enter lead name"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Email</Label>
              <Input
                type="email"
                value={leadForm.email || ''}
                onChange={e => setLeadForm({ ...leadForm, email: e.target.value })}
                placeholder="Enter email"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Phone</Label>
              <Input
                value={leadForm.phoneNumber || ''}
                onChange={e => setLeadForm({ ...leadForm, phoneNumber: e.target.value })}
                placeholder="Enter phone number"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Department</Label>
              <Input
                value={leadForm.department || ''}
                onChange={e => setLeadForm({ ...leadForm, department: e.target.value })}
                placeholder="Enter department"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLeadDialog(false)} className="text-zinc-400 hover:text-white">
              Cancel
            </Button>
            <Button onClick={saveLead} disabled={saving} className="bg-brand-500 text-white hover:bg-brand-400">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PROJECT TYPE DIALOG */}
      <Dialog open={showTypeDialog} onOpenChange={setShowTypeDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {editingTypeId ? 'Edit Project Type' : 'Add New Project Type'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Type Value *</Label>
              <Input
                value={typeForm.typeValue}
                onChange={e => setTypeForm({ ...typeForm, typeValue: e.target.value })}
                placeholder="Enter type value (e.g., staff_aug)"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Type Label *</Label>
              <Input
                value={typeForm.typeLabel}
                onChange={e => setTypeForm({ ...typeForm, typeLabel: e.target.value })}
                placeholder="Enter type label (e.g., Staff Augmentation)"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Description</Label>
              <Input
                value={typeForm.description || ''}
                onChange={e => setTypeForm({ ...typeForm, description: e.target.value })}
                placeholder="Enter description"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowTypeDialog(false)} className="text-zinc-400 hover:text-white">
              Cancel
            </Button>
            <Button onClick={saveType} disabled={saving} className="bg-brand-500 text-white hover:bg-brand-400">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CATEGORY DIALOG */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope text-white">
              {editingCategoryId ? 'Edit Category' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Category Value *</Label>
              <Input
                value={categoryForm.categoryValue}
                onChange={e => setCategoryForm({ ...categoryForm, categoryValue: e.target.value })}
                placeholder="Enter category value (e.g., regular)"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Category Label *</Label>
              <Input
                value={categoryForm.categoryLabel}
                onChange={e => setCategoryForm({ ...categoryForm, categoryLabel: e.target.value })}
                placeholder="Enter category label (e.g., Regular)"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-zinc-500 font-manrope mb-1.5 block">Description</Label>
              <Input
                value={categoryForm.description || ''}
                onChange={e => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Enter description"
                className="bg-zinc-950 border-zinc-800 focus:border-brand-500 text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCategoryDialog(false)} className="text-zinc-400 hover:text-white">
              Cancel
            </Button>
            <Button onClick={saveCategory} disabled={saving} className="bg-brand-500 text-white hover:bg-brand-400">
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
